/** Formatting helpers shared by the web portal, the desktop app and the mobile app. */

/**
 * Formatting locale.
 *
 * Explicitly *not* `undefined`, which would make Intl follow the host OS. A
 * machine set to ar-EG then renders "قبل 42 ثانية" and Arabic-Indic digits
 * beside English UI labels — the numbers stop matching the interface around
 * them. The portal sets this from the signed-in user's `locale` preference.
 */
let activeLocale = 'en';

export function setLocale(locale: string | undefined | null): void {
  activeLocale = locale && locale.trim() ? locale : 'en';
}

export function getLocale(): string {
  return activeLocale;
}

export function formatBytes(bytes: number, decimals = 1): string {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : decimals)} ${units[i]}`;
}

export function formatBitsPerSec(bps: number, decimals = 1): string {
  if (!Number.isFinite(bps)) return '—';
  if (bps === 0) return '0 bps';
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(Math.abs(bps)) / Math.log(1000)));
  return `${(bps / 1000 ** i).toFixed(i === 0 ? 0 : decimals)} ${units[i]}`;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

/** Call-timer style mm:ss / h:mm:ss, for live call displays. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

/**
 * English-only fallback for engines with no `Intl.RelativeTimeFormat` —
 * notably Hermes on Android, which ships without it even though it has
 * `Intl.NumberFormat` and `Intl.DateTimeFormat`. Every other caller gets the
 * locale-aware path below; this only runs where the real API is absent.
 */
function formatRelativeFallback(diff: number, abs: number, unit: 'second' | 'minute' | 'hour' | 'day'): string {
  const n = Math.abs(diff);
  const label = `${n} ${unit}${n === 1 ? '' : 's'}`;
  return diff <= 0 ? `${label} ago` : `in ${label}`;
}

export function formatRelative(ts: number, now = Date.now()): string {
  const diff = Math.round((ts - now) / 1000);
  const abs = Math.abs(diff);
  const rtf = typeof Intl.RelativeTimeFormat === 'function' ? new Intl.RelativeTimeFormat(activeLocale, { numeric: 'auto' }) : null;
  if (abs < 60) return rtf ? rtf.format(diff, 'second') : formatRelativeFallback(diff, abs, 'second');
  if (abs < 3600) {
    const v = Math.round(diff / 60);
    return rtf ? rtf.format(v, 'minute') : formatRelativeFallback(v, abs, 'minute');
  }
  if (abs < 86400) {
    const v = Math.round(diff / 3600);
    return rtf ? rtf.format(v, 'hour') : formatRelativeFallback(v, abs, 'hour');
  }
  if (abs < 2592000) {
    const v = Math.round(diff / 86400);
    return rtf ? rtf.format(v, 'day') : formatRelativeFallback(v, abs, 'day');
  }
  return new Date(ts).toLocaleDateString(activeLocale);
}

export function formatMetric(value: number | null | undefined, unit: string | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  switch (unit) {
    case 'percent':
      return `${value.toFixed(value < 10 ? 1 : 0)}%`;
    case 'bytes':
      return formatBytes(value);
    case 'bytes_per_sec':
      return `${formatBytes(value)}/s`;
    case 'bits_per_sec':
      return formatBitsPerSec(value);
    case 'milliseconds':
      return value < 1 ? `${(value * 1000).toFixed(0)} µs` : `${value.toFixed(value < 10 ? 1 : 0)} ms`;
    case 'seconds':
      return formatDuration(value);
    case 'celsius':
      return `${value.toFixed(1)} °C`;
    case 'ratio':
      return `${(value * 100).toFixed(1)}%`;
    default:
      return new Intl.NumberFormat(activeLocale, { maximumFractionDigits: 2 }).format(value);
  }
}
